import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardList, Gift } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { PlanCards, ServiceCards } from "@/components/fo/PlanCards";
import { BriefDialog } from "@/components/fo/BriefDialog";

const NAMES = { premiere: "Radio Premiere", review: "Written Review", cover: "Home Cover (7 days)", interview: "Published Interview", feature: "Artist of the Month feature" };
const needsBrief = (o, briefs) => o.status !== "delivered" && !o.brief && briefs[o.service];
const status = (o) => (o.status === "delivered" ? "Delivered" : o.brief ? "In progress" : "Brief needed");

const BriefBanner = ({ orders, open }) => !orders.length ? null : (
  <div className="panel relative overflow-hidden border-[#FF2E63]/40 p-6" data-testid="brief-banner">
    <div className="absolute inset-y-0 left-0 w-1 bg-[#FF2E63]" />
    <div className="label-mono flex items-center gap-2 text-[#FF2E63]"><ClipboardList className="h-3.5 w-3.5" />Action needed</div>
    <p className="mt-2 font-display text-xl font-bold">Fill in the brief so we can start working</p>
    <div className="mt-4 flex flex-wrap gap-2">
      {orders.map((o) => <button key={o.id} className="btn-hot !px-4 !py-2 text-sm" onClick={() => open(o)} data-testid={`brief-open-${o.id}`}>{NAMES[o.service]}</button>)}
    </div>
  </div>
);

export const PlanTab = ({ d, reload }) => {
  const [params, setParams] = useSearchParams();
  const [cur, setCur] = useState(null);
  const briefs = d.briefs || {};
  const pending = d.orders.filter((o) => needsBrief(o, briefs));
  useEffect(() => {
    const id = params.get("brief");
    const o = id && d.orders.find((x) => x.id === id);
    if (o) setCur(o);
  }, [params, d.orders]);
  const close = () => { setCur(null); if (params.get("brief")) { params.delete("brief"); setParams(params, { replace: true }); } };
  const paid = async () => {
    const before = new Set(d.orders.map((o) => o.id));
    const fresh = await reload();
    const o = fresh?.orders?.find((x) => !before.has(x.id) && briefs[x.service]);
    if (o) setCur(o);
  };
  const cancel = async () => { await api.post("/me/plan/cancel"); toast.success("Auto-renew cancelled. Your plan stays active until it expires."); reload(); };
  return (
    <div className="space-y-12" data-testid="dash-plan">
      <BriefBanner orders={pending} open={setCur} />
      <div className="panel flex flex-wrap items-center justify-between gap-4 p-6" data-testid="current-plan-panel">
        <div>
          <div className="label-mono">Current plan</div>
          <div className="mt-2 font-display text-2xl font-bold">{d.plan.name}{d.plan.until && <span className="ml-2 font-mono text-sm text-slate-400">until {fmtDate(d.plan.until)}</span>}</div>
          {d.user.founding && <div className="mt-1 text-sm text-[#FFB800]">Founding Artist — your first 3 months of Pro are on us.</div>}
        </div>
        {d.plan.auto_renew && d.plan.id !== "free" && <button className="btn-ghost" onClick={cancel} data-testid="cancel-plan-button">Cancel auto-renew</button>}
      </div>
      <PlanCards current={d.plan.id} onPaid={reload} />
      <div>
        <div className="label-mono mb-4">One-off services</div>
        <ServiceCards tracks={d.tracks} onPaid={paid} />
      </div>
      <div className="panel p-6" data-testid="orders-list">
        <div className="label-mono mb-4">Your orders</div>
        {d.orders.length ? d.orders.map((o) => (
          <div key={o.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 py-3 text-sm" data-testid={`order-${o.id}`}>
            <span className="flex items-center gap-2">{NAMES[o.service] || o.service}{o.source === "award" && <span className="font-mono text-[10px] text-[#FFB800]">PRIZE</span>}{o.source === "gift" && <Gift className="h-3.5 w-3.5 text-[#00F5D4]" />}</span>
            <span className="flex items-center gap-3">
              <span className={`font-mono text-xs uppercase ${o.status === "delivered" ? "text-[#00F5D4]" : o.brief ? "text-[#FFB800]" : "text-[#FF2E63]"}`} data-testid={`order-status-${o.id}`}>{status(o)} · {fmtDate(o.created_at)}</span>
              {o.status !== "delivered" && briefs[o.service] && <button className="btn-ghost !px-3 !py-1 text-xs" onClick={() => setCur(o)} data-testid={`order-brief-${o.id}`}>{o.brief ? "Edit brief" : "Fill in brief"}</button>}
            </span>
          </div>
        )) : <p className="text-sm text-slate-500">No orders yet.</p>}
      </div>
      <BriefDialog order={cur} brief={cur && briefs[cur.service]} name={cur && NAMES[cur.service]} tracks={d.tracks} onClose={close} onSaved={reload} />
    </div>
  );
};
